import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO, generateServiceSchema } from '../../utils/seo';

export default function TransfersPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [formData, setFormData] = useState({
    depart: '',
    destination: '',
    date: '',
    passagers: '1'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useSEO({
    title: 'Transfert privé Genève – Lyon & stations de ski | Chauffeur professionnel 24h/24',
    description: 'Voyagez en toute sérénité entre Genève, Lyon et les stations de ski avec un service de transport privé fiable et confortable. Nos chauffeurs professionnels vous garantissent un trajet sécurisé, ponctuel et sans stress.',
    keywords: 'transfert privé Genève Lyon, chauffeur professionnel, transport stations ski, service 24h/24, ponctualité garantie',
    canonical: '/transfers',
    ogTitle: 'Transfert privé Genève – Lyon & stations de ski - Service Premium',
    ogDescription: 'Service de transport privé fiable et confortable. Chauffeurs professionnels, ponctualité garantie, disponible 24h/24.',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/transfers#webpage`,
          url: `${siteUrl}/transfers`,
          name: 'Transfert privé Genève – Lyon & stations de ski',
          description: 'Service de transport privé premium entre Genève, Lyon et stations de ski alpines',
          inLanguage: 'fr-FR',
        },
        generateServiceSchema(),
      ],
    },
  });

  const [servicesRef, servicesInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [tarifsRef, tarifsInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [confianceRef, confianceInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        depart: formData.depart,
        destination: formData.destination,
        date: formData.date,
        passagers: formData.passagers
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d5lair6m3t2gksp8cmfg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ depart: '', destination: '', date: '', passagers: '1' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* HERO SECTION */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/e717e432c36f90b8e2ff4875fbe4d63f.jpg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        
        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-playfair text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Transfert privé Genève – Lyon<br />& stations de ski
          </motion.h1>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-inter text-2xl lg:text-3xl font-semibold text-alpine-gold mb-6"
          >
            Chauffeur professionnel • Service 24h/24 • Ponctualité garantie
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-slate-100 font-inter text-lg lg:text-xl max-w-4xl mx-auto mb-10 leading-relaxed"
          >
            Voyagez en toute sérénité entre Genève, Lyon et les stations de ski avec un service de transport privé fiable et confortable. Nos chauffeurs professionnels vous garantissent un trajet sécurisé, ponctuel et sans stress.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to="/reservation"
              className="inline-flex items-center gap-3 px-10 py-5 bg-alpine-gold text-night-blue rounded-full font-inter font-bold text-lg hover:bg-white transition-all shadow-2xl whitespace-nowrap cursor-pointer"
            >
              <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
              Réserver maintenant
            </Link>
            <a
              href="https://wa.me/33605606006"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-10 py-5 bg-green-500 text-white rounded-full font-inter font-bold text-lg hover:bg-green-600 transition-all shadow-2xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              WhatsApp
            </a>
            <a
              href="#contact"
              className="inline-flex items-center gap-3 px-10 py-5 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-full font-inter font-bold text-lg hover:bg-white hover:text-night-blue transition-all whitespace-nowrap cursor-pointer"
            >
              Devis gratuit 2min
            </a>
          </motion.div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section ref={servicesRef} className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Nos services de transport privé
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={servicesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-3xl p-8 hover:shadow-2xl transition-all"
            >
              <div className="w-16 h-16 bg-alpine-gold/10 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-plane-line text-alpine-gold text-4xl"></i>
              </div>
              <h3 className="font-playfair text-2xl font-bold text-night-blue mb-4">
                ✈️ Aéroport Genève
              </h3>
              <p className="text-slate-600 font-inter text-base leading-relaxed">
                Arrivée/départ sans stress, suivi vols en temps réel, attente incluse. Service d'accueil personnalisé avec panneau nominatif.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={servicesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-3xl p-8 hover:shadow-2xl transition-all"
            >
              <div className="w-16 h-16 bg-alpine-gold/10 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-road-map-line text-alpine-gold text-4xl"></i>
              </div>
              <h3 className="font-playfair text-2xl font-bold text-night-blue mb-4">
                🛣️ Genève ⇄ Lyon
              </h3>
              <p className="text-slate-600 font-inter text-base leading-relaxed">
                Trajets rapides et confortables pour particuliers & professionnels. Idéal pour vos déplacements d'affaires ou personnels.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={servicesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-3xl p-8 hover:shadow-2xl transition-all"
            >
              <div className="w-16 h-16 bg-alpine-gold/10 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-mountain-line text-alpine-gold text-4xl"></i>
              </div>
              <h3 className="font-playfair text-2xl font-bold text-night-blue mb-4">
                🏔️ Stations de ski
              </h3>
              <p className="text-slate-600 font-inter text-base leading-relaxed">
                Courchevel, Chamonix, Megève, Val d'Isère... Véhicules spacieux adaptés pour vos skis et bagages volumineux.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TARIFS SECTION */}
      <section ref={tarifsRef} className="py-24 px-6 lg:px-12 bg-snow-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={tarifsInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Tarifs transparents à partir de
            </h2>
            <p className="text-slate-600 font-inter text-lg">
              Prix fixes sans surprise • Bagages et skis inclus
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={tarifsInView ? { opacity: 1, y: 0 } : {}}
            className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-200"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-night-blue text-white">
                  <tr>
                    <th className="px-6 py-5 text-left font-playfair text-xl font-bold">
                      Départ → Destination
                    </th>
                    <th className="px-6 py-5 text-center font-playfair text-xl font-bold">
                      Van 7 places
                    </th>
                    <th className="px-6 py-5 text-center font-playfair text-xl font-bold">
                      Berline 3p
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-5 font-inter text-night-blue font-medium">
                      Genève → Chamonix
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className="font-playfair text-2xl font-bold text-alpine-gold">270€</span>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className="font-playfair text-2xl font-bold text-alpine-gold">220€</span>
                    </td>
                  </tr>
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-5 font-inter text-night-blue font-medium">
                      Genève → Megève
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className="font-playfair text-2xl font-bold text-alpine-gold">280€</span>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className="font-playfair text-2xl font-bold text-alpine-gold">230€</span>
                    </td>
                  </tr>
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-5 font-inter text-night-blue font-medium">
                      Lyon → Courchevel
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className="font-playfair text-2xl font-bold text-alpine-gold">480€</span>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className="font-playfair text-2xl font-bold text-alpine-gold">420€</span>
                    </td>
                  </tr>
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-5 font-inter text-night-blue font-medium">
                      Grenoble → 2 Alpes
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className="font-playfair text-2xl font-bold text-alpine-gold">290€</span>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className="font-playfair text-2xl font-bold text-alpine-gold">250€</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="bg-slate-50 px-6 py-6 border-t border-slate-200">
              <p className="text-slate-600 font-inter text-sm text-center mb-4">
                <em>*Tarifs jour 7h-19h, bagages + skis inclus. Trajets de nuit +20%. Devis précis gratuit en 2 minutes.</em>
              </p>
              <div className="text-center">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-night-blue text-white rounded-full font-inter font-bold hover:bg-alpine-gold hover:text-night-blue transition-all whitespace-nowrap cursor-pointer"
                >
                  Devis personnalisé
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CONFIANCE SECTION */}
      <section ref={confianceRef} className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={confianceInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Pourquoi choisir mon service ?
            </h2>
            <p className="text-slate-600 font-inter text-lg">
              Transferts professionnels, fiables et confortables vers toutes les stations de ski
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { 
                icon: 'ri-user-star-line', 
                title: 'Chauffeur certifié et professionnel',
                text: 'Chauffeur licencié et expérimenté, avec une connaissance approfondie des routes de montagne et des stations de ski'
              },
              { 
                icon: 'ri-car-line', 
                title: 'Véhicule équipé pour la montagne',
                text: 'Véhicule confortable et spacieux, équipé de pneus hiver et de chaînes pour une conduite sécurisée en montagne'
              },
              { 
                icon: 'ri-user-line', 
                title: 'Transferts privés',
                text: 'Pas d\'attente, pas de trajet partagé. Transfert direct depuis votre point de prise en charge jusqu\'à votre destination'
              },
              { 
                icon: 'ri-luggage-cart-line', 
                title: 'Transport du matériel de ski',
                text: 'Grand espace pour tout votre matériel de ski, vos bagages et équipements inclus dans le prix'
              },
              { 
                icon: 'ri-price-tag-3-line', 
                title: 'Tarification claire',
                text: 'Prix transparents sans frais cachés. Obtenez votre devis à l\'avance et réservez en toute confiance'
              },
              { 
                icon: 'ri-smartphone-line', 
                title: 'Réservation facile',
                text: 'Processus de réservation en ligne simple. Réservez votre transfert en quelques clics seulement'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={confianceInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.1 * index }}
                className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 bg-alpine-gold/10 rounded-xl flex items-center justify-center flex-shrink-0 mb-4">
                  <i className={`${item.icon} text-alpine-gold text-2xl`}></i>
                </div>
                <h3 className="font-inter text-night-blue font-bold text-base mb-2">
                  {item.title}
                </h3>
                <p className="font-inter text-slate-600 text-sm leading-relaxed">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section id="contact" className="py-24 px-6 lg:px-12 bg-gradient-to-br from-night-blue via-slate-900 to-night-blue">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-white mb-4">
              Réservez votre transfert dès maintenant
            </h2>
            <p className="text-slate-300 font-inter text-xl">
              Réponse sous 2 minutes • Confirmation immédiate
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8"
            >
              <h3 className="font-playfair text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <i className="ri-phone-line text-alpine-gold"></i>
                Contact direct
              </h3>
              <div className="space-y-6">
                <div>
                  <p className="text-slate-400 font-inter text-sm mb-2">WhatsApp / Téléphone</p>
                  <a
                    href="https://wa.me/33605606006"
                    className="text-alpine-gold font-inter text-2xl font-bold hover:text-white transition-colors block mb-2"
                  >
                    +33 6 05 60 60 06
                  </a>
                  <a
                    href="https://wa.me/33605606006"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg font-inter text-sm font-semibold hover:bg-green-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-lg"></i>
                    Contacter sur WhatsApp
                  </a>
                </div>
                <div>
                  <p className="text-slate-400 font-inter text-sm mb-2">Email</p>
                  <a
                    href="mailto:contac.driver@outlook.fr"
                    className="text-alpine-gold font-inter text-xl font-semibold hover:text-white transition-colors block mb-1"
                  >
                    contac.driver@outlook.fr
                  </a>
                </div>
                <div className="pt-6 border-t border-white/10">
                  <p className="text-slate-300 font-inter text-sm leading-relaxed">
                    Notre équipe est disponible 24h/24 et 7j/7 pour répondre à toutes vos demandes de transfert.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-2xl"
            >
              <h3 className="font-playfair text-2xl font-bold text-night-blue mb-6">
                Formulaire rapide
              </h3>
              <form onSubmit={handleSubmit} data-readdy-form id="transfer-booking-form">
                <div className="space-y-4">
                  <div>
                    <label className="block text-slate-700 font-inter text-sm font-medium mb-2">
                      Départ
                    </label>
                    <input
                      type="text"
                      name="depart"
                      value={formData.depart}
                      onChange={(e) => setFormData({ ...formData, depart: e.target.value })}
                      placeholder="Ex: Genève Aéroport"
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl font-inter text-sm focus:outline-none focus:ring-2 focus:ring-alpine-gold"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-700 font-inter text-sm font-medium mb-2">
                      Destination
                    </label>
                    <input
                      type="text"
                      name="destination"
                      value={formData.destination}
                      onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                      placeholder="Ex: Chamonix"
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl font-inter text-sm focus:outline-none focus:ring-2 focus:ring-alpine-gold"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-700 font-inter text-sm font-medium mb-2">
                      Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl font-inter text-sm focus:outline-none focus:ring-2 focus:ring-alpine-gold"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-700 font-inter text-sm font-medium mb-2">
                      Nombre de passagers
                    </label>
                    <select
                      name="passagers"
                      value={formData.passagers}
                      onChange={(e) => setFormData({ ...formData, passagers: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl font-inter text-sm focus:outline-none focus:ring-2 focus:ring-alpine-gold"
                    >
                      {[1, 2, 3, 4, 5, 6, 7].map(num => (
                        <option key={num} value={num}>{num} passager{num > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-night-blue text-white py-4 rounded-xl font-inter font-bold text-base hover:bg-alpine-gold hover:text-night-blue transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl font-inter text-sm">
                      ✓ Votre demande a été envoyée avec succès ! Nous vous répondrons sous 2 minutes.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl font-inter text-sm">
                      ✗ Une erreur est survenue. Veuillez réessayer ou nous contacter directement.
                    </div>
                  )}
                </div>
              </form>
            </motion.div>
          </div>

          {/* CTA Final */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link
              to="/reservation"
              className="inline-flex items-center gap-3 px-12 py-5 bg-alpine-gold text-night-blue rounded-full font-inter font-bold text-xl hover:bg-white transition-all shadow-2xl whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 bg-green-500 rounded-full animate-pulse"></span>
              Réserver maintenant
              <i className="ri-arrow-right-line"></i>
            </Link>
            <a
              href="https://wa.me/33605606006"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-12 py-5 bg-green-500 text-white rounded-full font-inter font-bold text-xl hover:bg-green-600 transition-all shadow-2xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              WhatsApp
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
